import { useDocumentRouteReady } from "../components/RouteCanvas";
import { useEffect, useRef, useState } from "react";

import { AGING_BUCKETS, AGING_BUCKET_LABELS, type AccountsAgingReport, type AgingBucket } from "../../shared/reporting";
import { useSession } from "../auth/session";
import { Button } from "../components/Button";
import { Notice } from "../components/Feedback";
import { NavLink } from "../components/NavLink";
import { formatMoney } from "./invoiceApi";
import { downloadReportCsv, ReportHeader, ReportTable, requestReport, setReportSearch, useReportSearch } from "./ReportPage";

type SelectedBucket = AgingBucket | "all";

export function AccountsAgingPage() {
  const { getToken, state } = useSession(), token = useRef(getToken); token.current = getToken;
  const search = useReportSearch(), query = search.replace(/^\?/, "");
  const [result, setResult] = useState<AccountsAgingReport | null>(null);
  const [loading, setLoading] = useState(true);
  useDocumentRouteReady(!loading);
  const [error, setError] = useState("");

  useEffect(() => {
    if (state !== "signed-in") return;
    const controller = new AbortController();
    setLoading(true); setError(""); setResult(null);
    void requestReport(token.current, "accounts-aging", query, controller.signal).then(report => {
      if (report.kind !== "accounts-aging") throw new Error("Accounts Aging could not be loaded.");
      if (!controller.signal.aborted) setResult(report);
    }).catch(failure => {
      if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : "Accounts Aging could not be loaded.");
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [query, state]);

  function select(clientId: string | null, bucket: SelectedBucket) {
    const parameters = new URLSearchParams();
    if (clientId) parameters.set("client", clientId);
    parameters.set("bucket", bucket);
    setReportSearch(parameters.toString());
  }

  function amount(value: number, clientId: string | null, clientName: string, bucket: SelectedBucket) {
    if (!value) return "—";
    const selected = result?.filters.clientId === clientId && result.filters.bucket === bucket;
    const bucketLabel = bucket === "all" ? "All aging" : AGING_BUCKET_LABELS[bucket];
    return <button type="button" className="sbx-sort report-aging-amount" aria-pressed={selected}
      aria-label={`${clientName} · ${bucketLabel} · ${formatMoney(value)}`}
      data-client-id={clientId ?? "all"} data-bucket={bucket}
      onClick={() => select(clientId, bucket)}>{formatMoney(value)}</button>;
  }

  const selectedClient = result?.filters.clientId
    ? result.rows.find(row => row.clientId === result.filters.clientId)?.clientName ?? "Selected client"
    : null;
  const selectedBucket = result?.filters.bucket;
  const detailContext = selectedClient
    ? `${selectedClient} · ${selectedBucket === "all" ? "All aging" : selectedBucket ? AGING_BUCKET_LABELS[selectedBucket] : ""}`
    : selectedBucket === "all" ? "All clients" : selectedBucket ? AGING_BUCKET_LABELS[selectedBucket] : "";

  const selectedAmounts = selectedClient ? result?.rows.find(row => row.clientId === result.filters.clientId) : result?.summary;
  const selectedTotal = selectedBucket === "all" ? selectedAmounts?.totalMinor : selectedBucket ? selectedAmounts?.buckets[selectedBucket] : null;
  function cellClass(clientId: string | null, bucket: SelectedBucket) {
    return `sbx-amount${result?.filters.clientId === clientId && result.filters.bucket === bucket ? " is-selected" : ""}`;
  }

  return <div className="clients-page report-page">
    <ReportHeader title="Accounts Aging" description="Current outstanding Invoice balances by client and due date."
      action={<Button variant="secondary" disabled={loading || !result} onClick={() => result && downloadReportCsv(result)}>Export CSV</Button>} />
    {error ? <Notice title="Report unavailable" tone="danger">{error}</Notice> : null}
    {loading ? <p role="status">Loading report…</p> : null}
    {result ? <>
      <p className="sbx-cell-secondary report-range">As of {result.currentDate}</p>
      <ReportTable title="Accounts Aging" className="report-aging" headings={["Client", "Current", "1–30", "31–60", "61–90", "90+", "Total"]}
        footer={<tr><th scope="row">Total</th>
          {AGING_BUCKETS.map(bucket => <td key={bucket} data-label={AGING_BUCKET_LABELS[bucket].replace(" days", "")} className={cellClass(null, bucket)}>{amount(result.summary.buckets[bucket], null, "All clients", bucket)}</td>)}
          <td className={`${cellClass(null, "all")} report-aging-total`}>{amount(result.summary.totalMinor, null, "All clients", "all")}</td>
        </tr>}>
        {result.rows.length ? result.rows.map(client => <tr key={client.clientId} data-client-id={client.clientId}>
          <th scope="row">{client.clientName ?? "—"}</th>
          {AGING_BUCKETS.map(bucket => <td key={bucket} data-label={AGING_BUCKET_LABELS[bucket].replace(" days", "")} className={cellClass(client.clientId, bucket)}>{amount(client.buckets[bucket], client.clientId, client.clientName ?? "—", bucket)}</td>)}
          <td className={`${cellClass(client.clientId, "all")} report-aging-total`}>{amount(client.totalMinor, client.clientId, client.clientName ?? "—", "all")}</td>
        </tr>) : <tr><td colSpan={7}>No outstanding invoices.</td></tr>}
      </ReportTable>
      {selectedBucket ? <section className="report-aging-details" aria-labelledby="aging-detail-heading">
        <header className="sbx-register-section">
          <div><h2 id="aging-detail-heading">Outstanding invoices</h2><p>{detailContext} · {formatMoney(selectedTotal ?? 0)}</p></div>
          <Button variant="quiet" onClick={() => setReportSearch("")}>Clear selection</Button>
        </header>
        <ReportTable title="Outstanding invoices" className="report-aging-invoices" headings={["Invoice #", "Client", "Due", "Days past due", "Bucket", "Invoice total", "Paid", "Amount due", "Archived"]}>
          {result.details.length ? result.details.map(invoice => <tr key={invoice.invoiceId} data-invoice-id={invoice.invoiceId}>
            <td className="report-aging-number"><NavLink active={false} href={`/invoices/${invoice.invoiceId}`}>{invoice.number}</NavLink></td>
            <td className="report-aging-client">{invoice.clientName ?? "—"}<span className="report-mobile-only"> · {invoice.daysPastDue} days past due · {AGING_BUCKET_LABELS[invoice.bucket]}</span></td>
            <td className="report-aging-dates"><span className="report-mobile-only">Issued {invoice.issueDate} · Due </span>{invoice.dueDate}</td>
            <td className="report-aging-days">{invoice.daysPastDue}</td>
            <td className="report-aging-bucket">{AGING_BUCKET_LABELS[invoice.bucket]}</td>
            <td className="sbx-amount report-aging-invoice-total"><span className="report-mobile-only">Invoice total </span>{formatMoney(invoice.totalMinor)}<span className="report-mobile-only"> · Paid {formatMoney(invoice.paidMinor)} · {invoice.archived ? "Archived" : "Not archived"}</span></td>
            <td className="sbx-amount report-aging-paid"><span className="report-mobile-only">Paid </span>{formatMoney(invoice.paidMinor)}</td>
            <td className="sbx-amount report-aging-due">{formatMoney(invoice.dueMinor)}</td>
            <td className="report-aging-archived">{invoice.archived ? "Archived" : "—"}</td>
          </tr>) : <tr><td colSpan={9}>No outstanding invoices match this selection.</td></tr>}
        </ReportTable>
      </section> : null}
    </> : null}
  </div>;
}
